import { useEffect, useMemo, useRef, useState } from "react";
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  createSeriesMarkers,
  LineStyle,
  CrosshairMode,
} from "lightweight-charts";
import { Lock } from "lucide-react";
import { getHistory, getQuote } from "@/lib/api";
import { pricePrecision, friendlyName } from "@/lib/symbols";

/**
 * Professional TradingView-style candlestick chart for the NUMIROCK desk.
 *   - OHLC candles (green/red, matches TradingView palette)
 *   - Volume histogram in a sub-pane
 *   - SMA-20 gold overlay
 *   - Dashed last-price line with a gold axis price tag
 *   - Crosshair + OHLC tooltip
 *   - Timeframe selector (1D / 5D / 1M / 6M / 1Y)
 *   - Graceful "interval unavailable" chip when yfinance falls back to daily
 *   - `autoMode`: when true, SELL / BUY are locked and replaced with a
 *     lock chip so the user gets a clear reason the buttons are disabled
 *     (backend also returns 409 for defense-in-depth).
 */

const TIMEFRAMES = [
  { key: "1D",  period: "1d",   interval: "15m", smallLabel: "15m bars"  },
  { key: "5D",  period: "5d",   interval: "1h",  smallLabel: "1h bars"   },
  { key: "1M",  period: "1mo",  interval: "1d",  smallLabel: "daily"     },
  { key: "3M",  period: "3mo",  interval: "1d",  smallLabel: "daily"     },
  { key: "6M",  period: "6mo",  interval: "1d",  smallLabel: "daily"     },
  { key: "YTD", period: "ytd",  interval: "1d",  smallLabel: "daily"     },
  { key: "1Y",  period: "1y",   interval: "1wk", smallLabel: "weekly"    },
  { key: "ALL", period: "max",  interval: "1mo", smallLabel: "monthly"   },
];

// NUMIROCK brand palette — up=gold, down=metallic purple.
const UP = "#C9A227";
const DOWN = "#A085E6";
const GOLD = "#C9A227";
const SMA_LINE = "#D9C46F";  // pale gold for the SMA overlay

function toUnixTime(iso) {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}

function computeSMA(closes, window = 20) {
  const out = new Array(closes.length).fill(null);
  let sum = 0;
  for (let i = 0; i < closes.length; i++) {
    sum += closes[i];
    if (i >= window) sum -= closes[i - window];
    if (i >= window - 1) out[i] = sum / window;
  }
  return out;
}

export default function ChartPro({ symbol, onTradeIntent, autoMode = false }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const volumeRef = useRef(null);
  const smaRef = useRef(null);
  const tooltipRef = useRef(null);
  const precisionRef = useRef(pricePrecision(symbol));
  const [tf, setTf] = useState("1M");
  const [meta, setMeta] = useState(null);
  const [candles, setCandles] = useState([]);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);
  const [fallback, setFallback] = useState(null);
  const [loading, setLoading] = useState(false);

  // Keep the price precision fresh when the symbol changes without rebuilding
  // the chart.
  useEffect(() => {
    precisionRef.current = pricePrecision(symbol);
    if (chartRef.current) {
      chartRef.current.applyOptions({
        localization: {
          locale: "en-US",
          priceFormatter: (p) => Number(p).toFixed(precisionRef.current),
        },
      });
    }
  }, [symbol]);

  const active = useMemo(
    () => TIMEFRAMES.find((t) => t.key === tf) || TIMEFRAMES[2],
    [tf],
  );

  // Fetch quote + candles whenever symbol / timeframe changes
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setErr(null);
    setLoading(true);
    setFallback(null);
    Promise.all([
      getQuote(symbol).catch(() => null),
      getHistory(symbol, active.period, active.interval),
    ])
      .then(([q, h]) => {
        if (cancelled) return;
        setQuote(q);
        setMeta({ symbol: h.symbol, interval: h.interval, period: h.period });
        setFallback(h.fallback_from_interval || null);
        const rows = (h.candles || [])
          .filter((c) => c.open != null && c.high != null && c.low != null && c.close != null)
          .map((c) => ({
            time: toUnixTime(c.timestamp),
            open: Number(c.open),
            high: Number(c.high),
            low: Number(c.low),
            close: Number(c.close),
            volume: Number(c.volume || 0),
          }))
          .filter((c) => c.time != null);
        setCandles(rows);
      })
      .catch((e) =>
        setErr(e?.response?.data?.detail || e.message || "chart unavailable"),
      )
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, active.period, active.interval]);

  // Build the chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      localization: {
        locale: "en-US",
        priceFormatter: (p) => Number(p).toFixed(precisionRef.current),
      },
      layout: {
        background: { type: "solid", color: "transparent" },
        textColor: "#A9A9B6",
        fontFamily:
          "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.02)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#8B6CC1", width: 1, style: LineStyle.Dashed },
        horzLine: { color: "#8B6CC1", width: 1, style: LineStyle.Dashed, labelBackgroundColor: "#8B6CC1" },
      },
      timeScale: {
        borderColor: "#2A2A35",
        rightOffset: 6,
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 5,        // dense professional terminal look
        minBarSpacing: 2,
      },
      rightPriceScale: {
        borderColor: "#2A2A35",
      },
      autoSize: true,
    });
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: UP,
      downColor: DOWN,
      wickUpColor: UP,
      wickDownColor: DOWN,
      borderUpColor: UP,
      borderDownColor: DOWN,
      // Thin wicks + tight bodies for a dense terminal aesthetic.
      wickVisible: true,
      priceLineVisible: true,
      priceLineColor: GOLD,
      priceLineStyle: LineStyle.Dashed,
      priceLineWidth: 1,
      lastValueVisible: true,
    });
    const volume = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
      color: "#3a3a45",
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });
    const sma = chart.addSeries(LineSeries, {
      color: SMA_LINE,
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    chartRef.current = chart;
    candleRef.current = candle;
    volumeRef.current = volume;
    smaRef.current = sma;

    // Crosshair OHLC tooltip — floats top-left of the chart body, follows
    // the cursor and shows O/H/L/C + volume for the bar under the crosshair.
    const fmtPrice = (p) =>
      p == null ? "—" : Number(p).toFixed(precisionRef.current);
    const fmtVol = (v) => {
      if (v == null) return "—";
      if (v >= 1e9) return (v / 1e9).toFixed(2) + "B";
      if (v >= 1e6) return (v / 1e6).toFixed(2) + "M";
      if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
      return String(Math.round(v));
    };
    chart.subscribeCrosshairMove((param) => {
      const tip = tooltipRef.current;
      if (!tip) return;
      if (
        !param.time ||
        !param.point ||
        param.point.x < 0 ||
        param.point.y < 0
      ) {
        tip.style.display = "none";
        return;
      }
      const bar = param.seriesData.get(candle);
      const volBar = param.seriesData.get(volume);
      if (!bar) {
        tip.style.display = "none";
        return;
      }
      const up = bar.close >= bar.open;
      tip.style.display = "block";
      tip.innerHTML = `
        <div class="chart-tt-row">
          <span class="chart-tt-k">O</span><span class="chart-tt-v">${fmtPrice(bar.open)}</span>
          <span class="chart-tt-k">H</span><span class="chart-tt-v">${fmtPrice(bar.high)}</span>
          <span class="chart-tt-k">L</span><span class="chart-tt-v">${fmtPrice(bar.low)}</span>
          <span class="chart-tt-k">C</span><span class="chart-tt-v" style="color:${up ? UP : DOWN}">${fmtPrice(bar.close)}</span>
          <span class="chart-tt-k">V</span><span class="chart-tt-v">${fmtVol(volBar?.value)}</span>
        </div>`;
    });

    const onResize = () => chart.applyOptions({});
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      volumeRef.current = null;
      smaRef.current = null;
    };
  }, []);

  // Push data into the chart when candles change
  useEffect(() => {
    if (!candleRef.current || !volumeRef.current || !smaRef.current) return;
    if (!candles.length) {
      candleRef.current.setData([]);
      volumeRef.current.setData([]);
      smaRef.current.setData([]);
      return;
    }
    candleRef.current.setData(
      candles.map((c) => ({
        time: c.time, open: c.open, high: c.high, low: c.low, close: c.close,
      })),
    );
    volumeRef.current.setData(
      candles.map((c) => ({
        time: c.time,
        value: c.volume,
        // Brand palette: gold for up-days, metallic purple for down-days.
        color: c.close >= c.open
          ? "rgba(201,162,39,0.35)"
          : "rgba(160,133,230,0.35)",
      })),
    );
    const smaVals = computeSMA(candles.map((c) => c.close), 20);
    smaRef.current.setData(
      candles
        .map((c, i) => (smaVals[i] == null ? null : { time: c.time, value: smaVals[i] }))
        .filter(Boolean),
    );
    // Keep bars thin (fitContent would stretch them to fill the chart).
    chartRef.current?.timeScale().applyOptions({ barSpacing: 5, minBarSpacing: 2 });
    chartRef.current?.timeScale().scrollToRealTime();
  }, [candles]);

  const last = candles[candles.length - 1];
  const first = candles[0];
  const chgPct = quote?.change_pct != null
    ? Number(quote.change_pct)
    : first && last
    ? ((last.close - first.close) / first.close) * 100
    : 0;
  const up = chgPct >= 0;
  const displayPrice = quote?.price ?? last?.close;

  return (
    <div className="flex h-full flex-col" data-testid="chart-pro">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-3 border-b border-[var(--aurion-border)] px-3 pb-2 pt-2">
        <div className="flex items-baseline gap-3 min-w-0">
          <span
            data-testid="chart-symbol"
            className="font-heading text-base tracking-widest text-[var(--aurion-text)] truncate"
            title={friendlyName(symbol)}
          >
            {friendlyName(symbol, symbol) || "—"}
          </span>
          {displayPrice != null ? (
            <span className="font-mono text-base tabular-nums text-[var(--aurion-text)]">
              {Number(displayPrice).toFixed(precisionRef.current)}
            </span>
          ) : null}
          {quote?.change_pct != null ? (
            <span
              className={`font-mono text-xs tabular-nums ${
                up ? "text-up" : "text-down"
              }`}
            >
              {up ? "+" : ""}
              {chgPct.toFixed(2)}%
            </span>
          ) : null}
          <span className="hidden sm:inline text-[9px] uppercase tracking-widest text-[var(--aurion-muted)]">
            {(meta?.interval || active.interval)} · SMA-20
          </span>
          {fallback ? (
            <span
              data-testid="chart-fallback-chip"
              className="ml-1 rounded-sm border border-[var(--aurion-amber)]/60 bg-[var(--aurion-amber)]/10 px-1.5 py-[1px] text-[9px] uppercase tracking-widest text-[var(--aurion-amber)]"
              title={`Interval ${fallback} unavailable — showing daily`}
            >
              {fallback} unavailable · daily
            </span>
          ) : null}
        </div>
        {/* SELL / BUY buttons + timeframe selector */}
        <div className="flex items-center gap-2">
          {onTradeIntent && displayPrice != null ? (
            autoMode ? (
              <div
                data-testid="chart-autopilot-lock"
                title="Autopilot is ON — pause Autopilot to trade manually"
                className="mr-2 flex items-center gap-1 border border-[var(--aurion-gold)]/50 bg-[var(--aurion-gold)]/10 px-2 py-[3px] font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--aurion-gold)]"
              >
                <Lock className="h-3 w-3" />
                <span>Autopilot ON</span>
              </div>
            ) : (
              <div className="mr-2 flex items-stretch gap-[1px]">
                <button
                  type="button"
                  data-testid="chart-sell-btn"
                  onClick={() => onTradeIntent({ side: "SELL", symbol, price: displayPrice })}
                  className="flex flex-col items-center border border-[var(--aurion-down)] px-2 py-[3px] text-[var(--aurion-down)] transition-colors hover:bg-[var(--aurion-down)]/10"
                  title={`Sell / close ${friendlyName(symbol, symbol)}`}
                >
                  <span className="font-heading text-[9px] tracking-[0.18em]">SELL</span>
                  <span className="font-mono text-[10px] tabular-nums">
                    {Number(displayPrice).toFixed(precisionRef.current)}
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="chart-buy-btn"
                  onClick={() => onTradeIntent({ side: "BUY", symbol, price: displayPrice })}
                  className="flex flex-col items-center bg-[var(--aurion-gold)] px-2 py-[3px] text-black transition-colors hover:bg-[var(--aurion-gold)]/90"
                  title={`Buy ${friendlyName(symbol, symbol)}`}
                >
                  <span className="font-heading text-[9px] tracking-[0.18em]">BUY</span>
                  <span className="font-mono text-[10px] tabular-nums">
                    {Number(displayPrice).toFixed(precisionRef.current)}
                  </span>
                </button>
              </div>
            )
          ) : null}
          {TIMEFRAMES.map((t) => (
            <button
              key={t.key}
              data-testid={`chart-tf-${t.key.toLowerCase()}`}
              onClick={() => setTf(t.key)}
              className={`px-2 py-[3px] font-mono text-[10px] uppercase tracking-widest transition-colors ${
                tf === t.key
                  ? "bg-[var(--aurion-gold)]/15 text-[var(--aurion-gold)] border border-[var(--aurion-gold)]/40"
                  : "border border-transparent text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
              }`}
            >
              {t.key}
            </button>
          ))}
        </div>
      </div>

      {/* Chart body */}
      <div className="relative min-h-0 flex-1">
        {err ? (
          <div className="flex h-full items-center justify-center text-xs text-[var(--aurion-red)]">
            {err}
          </div>
        ) : null}
        {loading && !candles.length ? (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
            Loading…
          </div>
        ) : null}
        <div ref={containerRef} className="absolute inset-0" />
        <div
          ref={tooltipRef}
          data-testid="chart-ohlc-tooltip"
          style={{ display: "none" }}
          className="chart-ohlc-tooltip pointer-events-none absolute left-2 top-2 border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]/95 px-2 py-1 font-mono text-[10px] tracking-tight text-[var(--aurion-text-dim)] shadow-md backdrop-blur-sm"
        />
      </div>
    </div>
  );
}
